import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { Platform } from '@angular/cdk/platform';
import { DomSanitizer } from '@angular/platform-browser';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { Router } from '@angular/router';
import { MAT_DATE_FORMATS } from '@angular/material/core';
import { MatIconRegistry } from '@angular/material/icon';
import { OAuthService } from 'angular-oauth2-oidc';
import { JwksValidationHandler } from 'angular-oauth2-oidc-jwks';

import { LocalStorageService } from '@aes/core/local-storage/local-storage.service';
import { authLogin } from '@aes/core/auth/auth.actions';
import { FuseConfigService } from '@fuse/services/config.service';
import { AclService } from '@aes/acl/acl.service';
import { FuseNavigationService } from '@fuse/components/navigation/navigation.service';
import { FuseSidebarService } from '@fuse/components/sidebar/sidebar.service';
import { FuseSplashScreenService } from '@fuse/services/splash-screen.service';
import { FuseTranslationLoaderService } from '@fuse/services/translation-loader.service';

import { environment } from '../environments/environment';
import { authPasswordFlowConfig } from './auth/auth-password-flow.config';
import { APP_CUSTOM_DATE_FORMATS } from './shared/time-picker';
import { authModuleConfig } from './auth';
import { INumberOfApprovalRequest, NumberOfApprovalRequest } from './shared/model/number-approval-request.model';
import { SvgIcon } from './shared/model/svg-icon.model';
import { ApprovalRequestApiService } from './hrm/approval-request/services';

@Component({
  selector: 'app',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  providers: [{ provide: MAT_DATE_FORMATS, useValue: APP_CUSTOM_DATE_FORMATS }],
})
export class AppComponent implements OnInit, OnDestroy {
  fuseConfig: any;
  navigation: any;
  enableThemeEdit = environment.enableThemeEdit;
  numberOfApprovalRequests: INumberOfApprovalRequest = new NumberOfApprovalRequest();
  // Private
  private readonly destroy$ = new Subject<void>();

  /**
   * Constructor
   *
   * @param {DOCUMENT} document
   * @param _store
   * @param _router
   * @param {FuseConfigService} _fuseConfigService
   * @param {FuseNavigationService} _fuseNavigationService
   * @param {FuseSidebarService} _fuseSidebarService
   * @param {FuseSplashScreenService} _fuseSplashScreenService
   * @param {FuseTranslationLoaderService} _fuseTranslationLoaderService
   * @param {Platform} _platform
   * @param {TranslateService} _translateService
   * @param _oauthService
   * @param _storageService
   * @param _oauthService
   * @param _storageService
   */
  constructor(
    @Inject(DOCUMENT) private document: any,
    private _store: Store<any>,
    private _router: Router,
    private _fuseConfigService: FuseConfigService,
    private _fuseNavigationService: FuseNavigationService,
    private _fuseSidebarService: FuseSidebarService,
    private _fuseSplashScreenService: FuseSplashScreenService,
    private _fuseTranslationLoaderService: FuseTranslationLoaderService,
    private _translateService: TranslateService,
    private _platform: Platform,
    private _oauthService: OAuthService,
    private _storageService: LocalStorageService,
    private _approvalRequestApiSrv: ApprovalRequestApiService,
    private _aclService: AclService,
    private _matIconRegistry: MatIconRegistry,
    private _domSanitizer: DomSanitizer
  ) {
    this._oauthService.configure(authPasswordFlowConfig);
    this._oauthService.tokenValidationHandler = new JwksValidationHandler();
    this._oauthService.setStorage(_storageService);

    // get current language is saved in the local storage
    const currentLanguage = this._storageService.getLanguage();

    // Add languages
    this._translateService.addLangs(['en', 'vn']);

    // Set the default language
    this._translateService.setDefaultLang(currentLanguage ? currentLanguage : 'vn');

    // Use a language
    this._translateService.use(currentLanguage ? currentLanguage : 'vn');

    // Get default navigation
    this.navigation = [];

    /**
     * ----------------------------------------------------------------------------------------------------
     * ngxTranslate Fix Start
     * ----------------------------------------------------------------------------------------------------
     */

    /**
     * If you are using a language other than the default one, i.e. Turkish in this case,
     * you may encounter an issue where some of the components are not actually being
     * translated when your app first initialized.
     *
     * This is related to ngxTranslate module and below there is a temporary fix while we
     * are moving the multi language implementation over to the Angular's core language
     * service.
     */

    // Set the default language to 'en' and then back to 'tr'.
    // '.use' cannot be used here as ngxTranslate won't switch to a language that's already
    // been selected and there is no way to force it, so we overcome the issue by switching
    // the default language back and forth.
    /**
     * setTimeout(() => {
     * this._translateService.setDefaultLang('en');
     * this._translateService.setDefaultLang('tr');
     * });
     */

    /**
     * ----------------------------------------------------------------------------------------------------
     * ngxTranslate Fix End
     * ----------------------------------------------------------------------------------------------------
     */

    // Add is-mobile class to the body if the platform is mobile
    if (this._platform.ANDROID || this._platform.IOS) {
      this.document.body.classList.add('is-mobile');
    }

    // register addtional Artstuff icons for <mat-icon>
    this.addSVGsforMatIcon();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Lifecycle hooks
  // -----------------------------------------------------------------------------------------------------

  /**
   * On init
   */
  ngOnInit(): void {
    this._aclService.canAbility();
    this._storageService.testLocalStorage();
    // Subscribe to config changes
    this._fuseConfigService.config.pipe(takeUntil(this.destroy$)).subscribe(config => {
      this.fuseConfig = config;

      // Boxed
      if (this.fuseConfig.layout.width === 'boxed') {
        this.document.body.classList.add('boxed');
      } else {
        this.document.body.classList.remove('boxed');
      }

      // Color theme - Use normal for loop for IE11 compatibility
      for (let i = 0; i < this.document.body.classList.length; i++) {
        const className = this.document.body.classList[i];

        if (className.startsWith('theme-')) {
          this.document.body.classList.remove(className);
        }
      }

      this.document.body.classList.add(this.fuseConfig.colorTheme);
    });

    // Check access token
    if (this._oauthService.hasValidAccessToken()) {
      this._oauthService.loadDiscoveryDocument().then(() => this._store.dispatch(authLogin()));
    } else {
      this._router.navigate([authModuleConfig.loginPageRoute]);
    }

    this._translateService.onLangChange.subscribe((params: LangChangeEvent) => {
      if (params?.lang) {
        this._storageService.setLanguage(params.lang);
      }
    });
  }

  /**
   * On destroy
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this.destroy$.next();
    this.destroy$.complete();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Toggle sidebar open
   *
   * @param key
   */
  toggleSidebarOpen(key): void {
    this._fuseSidebarService.getSidebar(key).toggleOpen();
  }

  // register addtional Artstuff icons for <mat-icon>
  private addSVGsforMatIcon() {
    var svgIconArray = [
      new SvgIcon('artstuff_empty', '/assets/icons/artstuff/empty.svg'),
      new SvgIcon('artstuff_warning_red', '/assets/icons/artstuff/warning-red-vector.svg'),
      new SvgIcon('artstuff_delete_red', '/assets/icons/artstuff/delete-red-ector.svg'),
      new SvgIcon('artstuff_icon_copy', '/assets/icons/artstuff/icon_copy.svg'),
      new SvgIcon('artstuff_pin', '/assets/icons/artstuff/icon_pin.svg'),
      new SvgIcon('artstuff_stop', '/assets/icons/artstuff/icon_stop.svg'),
      new SvgIcon('artstuff_unpin', '/assets/icons/artstuff/icon_pin-off.svg'),
      new SvgIcon('artstuff_home', '/assets/icons/artstuff/home.svg'),
      new SvgIcon('artstuff_description', '/assets/icons/artstuff/description.svg'),
      new SvgIcon('artstuff_settings', '/assets/icons/artstuff/settings.svg'),
      new SvgIcon('artstuff_employee_info', '/assets/icons/artstuff/employee_info.svg'),
      new SvgIcon('artstuff_form_management', '/assets/icons/artstuff/form_management.svg'),
      new SvgIcon('artstuff_form_setting', '/assets/icons/artstuff/form_setting.svg'),
      new SvgIcon('artstuff_marketing', '/assets/icons/artstuff/marketing.svg'),
      new SvgIcon('artstuff_mkt_management', '/assets/icons/artstuff/mkt_management.svg'),
      new SvgIcon('artstuff_upload', '/assets/icons/artstuff/upload.svg'),
      new SvgIcon('artstuff_workingtime', '/assets/icons/artstuff/workingtime.svg'),
      new SvgIcon('artstuff_annual_leave', '/assets/icons/artstuff/annual_leave.svg'),
      new SvgIcon('lock_black', '/assets/images/general-config/lock_black.svg'),
      new SvgIcon('artstuff_success_response', 'assets/images/general-config/success_icon.svg'),
      new SvgIcon('artstuff_logo_google_drive', '/assets/images/approval-request/logo_google_drive.svg'),
      new SvgIcon('artstuff_cancel', 'assets/icons/artstuff/cancel.svg'),
      new SvgIcon('artstuff_leaderboard', 'assets/icons/artstuff/leaderboard.svg'),
      new SvgIcon('artstuff_reconciliation', 'assets/icons/artstuff/reconciliation.svg'),
      new SvgIcon('artstuff_cloud_queue', 'assets/icons/artstuff/cloud_queue.svg'),
      new SvgIcon('artstuff_not_support_display', 'assets/icons/artstuff/not_support_display.svg'),
      new SvgIcon('artstuff_download', 'assets/icons/artstuff/download.svg'),
      new SvgIcon('artstuff_inventory', 'assets/icons/artstuff/inventory.svg'),
      // SvgIcon for FileTypes
      new SvgIcon('ic_document', 'assets/icons/file-types/document.svg'),
      new SvgIcon('ic_doc', 'assets/icons/file-types/doc.svg'),
      new SvgIcon('ic_docx', 'assets/icons/file-types/docx.svg'),
      new SvgIcon('ic_pdf', 'assets/icons/file-types/pdf.svg'),
      new SvgIcon('ic_ppt', 'assets/icons/file-types/ppt.svg'),
      new SvgIcon('ic_pptx', 'assets/icons/file-types/pptx.svg'),
      new SvgIcon('ic_xls', 'assets/icons/file-types/xls.svg'),
      new SvgIcon('ic_xlsx', 'assets/icons/file-types/xlsx.svg'),
      new SvgIcon('ic_image', 'assets/icons/file-types/image.svg'),
    ];
    svgIconArray.forEach(svgIcon=>this._matIconRegistry.addSvgIcon(svgIcon.name,this._domSanitizer.bypassSecurityTrustResourceUrl(svgIcon.url)));
  }
}
